import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaGamepad } from 'react-icons/fa';
import ImageCard from '../components/ImageCard';
import RetroFreeDrawing from '../components/RetroFreeDrawing';
import RetroSpeedClicker from '../components/RetroSpeedClicker';
import RetroPrecisionClicker from '../components/RetroPrecisionClicker';
import RetroPixelChallenge from '../components/RetroPixelChallenge';
import RetroColorBubbles from '../components/RetroColorBubbles';
import RetroPixelDrawer from '../components/RetroPixelDrawer';
import RetroLetterCatcher from '../components/RetroLetterCatcher';

// Component mapping for game IDs
const gameComponents = {
  drawing: RetroFreeDrawing,
  speed: RetroSpeedClicker,
  precision: RetroPrecisionClicker,
  pixel: RetroPixelChallenge,
  bubbles: RetroColorBubbles,
  pixeldraw: RetroPixelDrawer,
  catcher: RetroLetterCatcher
};

const GameDemo = ({ gameId, onBack }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameResults, setGameResults] = useState(null);
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch game data based on gameId
  useEffect(() => {
    const fetchGame = async () => {
      if (!gameId) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gameconfig`);
        if (!response.ok) {
          throw new Error('Failed to fetch game configuration');
        }
        const config = await response.json();
        const foundGame = config.games.find(g => g.id === gameId);
        setGame(foundGame);
      } catch (error) {
        console.error('[RETRO-GAMEDEMO] ❌ Error fetching game:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [gameId]);

  const GameComponent = game ? gameComponents[game.id] : null;

  console.log('[RETRO-GAMEDEMO] 🎮 GameDemo render:', {
    gameId: game?.id,
    gameTitle: game?.title,
    isPlaying,
    hasGameComponent: !!GameComponent,
    timestamp: new Date().toISOString()
  });

  const handleStartGame = (e) => {
    console.log('[RETRO-GAMEDEMO] 🕹️ Starting game demo:', {
      gameId: game?.id,
      event: e,
      eventType: typeof e,
      hasStopPropagation: e && typeof e.stopPropagation === 'function',
      isPlaying,
      timestamp: new Date().toISOString()
    });
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    setIsPlaying(true);
  };

  const handleGameComplete = (results) => {
    console.log('[RETRO-GAMEDEMO] 🏁 Game demo completed:', {
      gameId: game?.id,
      results,
      timestamp: new Date().toISOString()
    });
    setGameResults(results);
  };

  const handlePlayAgain = () => {
    console.log('[RETRO-GAMEDEMO] 🔄 Restarting game demo:', game?.id);
    setIsPlaying(false);
    setGameResults(null);
    // Small delay to reset the component
    setTimeout(() => setIsPlaying(true), 100);
  };

  // Create a dummy image object for the ImageCard
  const demoImage = {
    id: `demo-${game?.id || 'unknown'}`,
    prompt: `${game?.title || 'Game'} Demo`,
    url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    s3Url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    thumbnailUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    timestamp: Date.now(),
    favoriteCount: 0,
    public: false,
    width: 512,
    height: 512
  };

  if (loading) {
    return (
      <div className="max-w-[920px] mx-auto">
        <div className="text-center py-12">
          <p className="text-black/60 font-['VT323'] text-lg">Loading game...</p>
        </div>
      </div>
    );
  }

  if (!game || !GameComponent) {
    return (
      <div className="max-w-[920px] mx-auto">
        <div className="text-center py-12">
          <p className="text-black/60 font-['VT323'] text-lg">Game not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[920px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <motion.button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Games
          </motion.button>
          <div>
            <h1 className="text-2xl font-['VT323'] text-black/60 flex items-center gap-2">
              <FaGamepad className="w-6 h-6" />
              {game?.title || 'Game'} Demo
            </h1>
            <p className="text-sm font-['VT323'] text-black/40">
              {game?.description || 'Loading...'}
            </p>
          </div>
        </div>

        {isPlaying && (
          <motion.button
            onClick={handlePlayAgain}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-600 hover:bg-blue-500/30 rounded-lg font-['VT323'] text-[18px] transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaGamepad className="w-4 h-4" />
            Play Again
          </motion.button>
        )}
      </div>

             {/* Debug Button (temporary) */}
       {!isPlaying && (
         <div className="flex justify-center mb-4">
           <motion.button
             onClick={handleStartGame}
             className="px-4 py-2 bg-green-500/20 text-green-600 hover:bg-green-500/30 rounded-lg font-['VT323'] text-lg transition-colors"
             whileHover={{ scale: 1.02 }}
             whileTap={{ scale: 0.98 }}
           >
             🚀 DEBUG: Start Game
           </motion.button>
         </div>
       )}

              {/* Game Demo Container */}
       <div className="flex justify-center">
         <div className="w-96 h-96 max-w-full relative bg-black rounded-xl overflow-hidden border-2 border-gray-700">
           {!isPlaying ? (
             // Show start screen
             <div 
               className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-gray-600 transition-colors"
               onClick={(e) => {
                 console.log('[RETRO-GAMEDEMO] 🖱️ Start screen clicked');
                 handleStartGame();
               }}
             >
               <motion.div
                 className="text-center"
                 whileHover={{ scale: 1.05 }}
                 whileTap={{ scale: 0.95 }}
               >
                 <div className="text-6xl mb-4">🎮</div>
                 <h2 className="font-['VT323'] text-2xl text-white mb-2">
                   {game?.title || 'Game'}
                 </h2>
                 <p className="font-['VT323'] text-lg text-gray-300 mb-4">
                   {game?.description || 'Loading...'}
                 </p>
                 <div className="px-6 py-3 bg-blue-500/20 text-blue-300 rounded-lg font-['VT323'] text-xl border border-blue-500/30 hover:bg-blue-500/30 transition-colors">
                   Click to Start Demo
                 </div>
               </motion.div>
             </div>
           ) : (
             // Show the actual game
             <div className="absolute inset-0 bg-black rounded-xl overflow-hidden">
               <div className="w-full h-full">
                 <GameComponent
                   isActive={true}
                   showStartButton={false}
                   onGameComplete={handleGameComplete}
                   skipSaveOnUnmount={true}
                   componentId={`demo-${game?.id || 'unknown'}-${Date.now()}`}
                   timeLimit={30} // Give a bit more time for demo
                 />
               </div>

               {/* Game Results Overlay */}
               {gameResults && (
                 <motion.div
                   initial={{ opacity: 0 }}
                   animate={{ opacity: 1 }}
                   className="absolute inset-0 bg-black/80 flex items-center justify-center"
                 >
                   <motion.div
                     initial={{ scale: 0.8, opacity: 0 }}
                     animate={{ scale: 1, opacity: 1 }}
                     className="bg-white rounded-xl p-6 text-center max-w-sm mx-4"
                   >
                     <h3 className="font-['VT323'] text-2xl text-black/70 mb-4">
                       Demo Complete!
                     </h3>
                     {gameResults.score !== undefined && (
                       <p className="font-['VT323'] text-xl text-black/60 mb-4">
                         Score: {gameResults.score}
                       </p>
                     )}
                     <div className="space-y-2">
                       <motion.button
                         onClick={handlePlayAgain}
                         className="w-full px-4 py-2 bg-blue-500/20 text-blue-600 hover:bg-blue-500/30 rounded-lg font-['VT323'] text-lg transition-colors"
                         whileHover={{ scale: 1.02 }}
                         whileTap={{ scale: 0.98 }}
                       >
                         Play Again
                       </motion.button>
                       <motion.button
                         onClick={onBack}
                         className="w-full px-4 py-2 bg-black/[0.03] text-black/60 hover:bg-black/[0.05] rounded-lg font-['VT323'] text-lg transition-colors"
                         whileHover={{ scale: 1.02 }}
                         whileTap={{ scale: 0.98 }}
                       >
                         Back to Games
                       </motion.button>
                     </div>
                   </motion.div>
                 </motion.div>
               )}
             </div>
           )}
         </div>
       </div>

      {/* Instructions */}
      <div className="mt-8 bg-black/[0.03] rounded-xl p-6">
        <h3 className="font-['VT323'] text-xl text-black/60 mb-3">
          How to Play
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-['VT323'] text-black/50">
          <div>
            <h4 className="text-lg text-black/60 mb-1">Game Controls:</h4>
            <p>Follow the on-screen instructions within the game</p>
          </div>
          <div>
            <h4 className="text-lg text-black/60 mb-1">Demo Purpose:</h4>
            <p>Test the game experience before making it available to users</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameDemo; 